/**
 * NutritionLabelFSSAI — print-style nutrition label panel that mirrors the
 * FSSAI Labelling and Display Regulations, which adopt Codex CAC/GL 2-1985
 * (Guidelines on Nutrition Labelling, latest amendments 2017).
 *
 * Layout matches the format specified in §3.4.6 / §3.4.7 of the Codex
 * guidelines:
 *
 *   ┌───────────────────────────────────────────────────────┐
 *   │  NUTRITIONAL INFORMATION                              │
 *   │  Serving size: …   Servings per package: …            │
 *   ├───────────────────┬──────────┬──────────┬─────────────┤
 *   │                   │ Per 100g │ Per serve│ %NRV per srv│
 *   │ Energy (kcal)     │   …      │   …      │     …       │
 *   │ Energy (kJ)       │   …      │   …      │             │
 *   │ Protein (g)       │   …      │   …      │     …       │
 *   │ Carbohydrate (g)  │   …      │   …      │     …       │
 *   │   of which sugars │   …      │   …      │             │
 *   │   added sugars    │   …      │   …      │     …       │
 *   │ Dietary fibre (g) │   …      │   …      │     …       │
 *   │ Total fat (g)     │   …      │   …      │     …       │
 *   │   saturated fat   │   …      │   …      │     …       │
 *   │   trans fat       │   …      │   …      │     …       │
 *   │   monounsaturated │   …      │   …      │             │
 *   │   polyunsaturated │   …      │   …      │             │
 *   │ Cholesterol (mg)  │   …      │   …      │             │
 *   │ Sodium (mg)       │   …      │   …      │     …       │
 *   │ Potassium (mg)    │   …      │   …      │     …       │
 *   ├───────────────────┴──────────┴──────────┴─────────────┤
 *   │ *NRV — Nutrient Reference Values per Codex CAC/GL 2-  │
 *   │  1985 (2017 amendment) for adults > 36 months on a    │
 *   │  2 000 kcal / 8 370 kJ reference diet.                │
 *   └───────────────────────────────────────────────────────┘
 *
 * BE-DRIVEN FIRST: pass `labelRows` (the server-computed `label_rows` from the
 * recipe-maker contract §6.1/§8.1) and the component renders them VERBATIM — no
 * client-side %NRV or kJ math on this path. A nutrition label is a regulatory
 * artifact; there must be exactly one computer of it, the server (contract §8.4).
 */
import { cn } from "@/lib/utils";

/**
 * Server-computed label row — mirrors the recipe-maker contract's `label_rows`
 * read shape (§6.1/§8.1). When the BE returns these, the FE renders them
 * VERBATIM (contract §8.4): no client-side %NRV or kJ math on the printable path.
 */
export interface FSSAILabelRow {
  key: string;
  label: string;
  unit: "kcal" | "kJ" | "g" | "mg";
  per_serve: number | null;
  per_100g: number | null;
  nrv_pct: number | null;
  bold: boolean;
  indent: boolean;
}

interface FSSAINutritionLabelProps {
  title?: string;
  /** Recipe / product name, shown above the mandatory heading. */
  productName?: string;
  servingLabel: string;
  servingsPerPackage: number;
  /** Server-computed `label_rows` rendered verbatim. */
  labelRows: FSSAILabelRow[];
  className?: string;
}

/** Format a numeric cell; render null as the em-dash "—". */
const fmtCell = (value: number | null, unit: FSSAILabelRow["unit"]): string => {
  if (value === null || Number.isNaN(value)) return "—";
  const digits = unit === "kcal" || unit === "kJ" ? 0 : 1;
  return value.toFixed(digits);
};

export const NutritionLabelFSSAI = ({
  title = "NUTRITIONAL INFORMATION",
  productName,
  servingLabel,
  servingsPerPackage,
  labelRows,
  className,
}: FSSAINutritionLabelProps) => {
  const rows: FSSAILabelRow[] = labelRows;

  // INTENTIONAL THEME DEVIATION (scoped to this component, do not propagate):
  // Real nutrition labels are legally required to be black-on-white for
  // legibility and to read as an authentic packaging artifact regardless of
  // the surrounding app theme. We use literal black + white instead of
  // foreground/card tokens, and pair it with a lifted shadow + soft outer
  // ring so it reads as a sticker peeled onto the page.
  return (
    <article
      className={cn(
        "w-full max-w-[300px] rounded-md border-2 border-black bg-white text-black",
        "font-sans select-text",
        "shadow-[0_8px_22px_-12px_rgba(0,0,0,0.45),0_2px_4px_-2px_rgba(0,0,0,0.25)]",
        "ring-1 ring-black/5",
        "print:shadow-none print:ring-0",
        className
      )}
    >
      <header className="px-2.5 pt-1.5 pb-1 border-b-2 border-black">
        {productName?.trim() ? (
          <p className="z-secondary leading-tight font-bold text-black">
            {productName.trim()}
          </p>
        ) : null}
        <h3
          className={cn(
            "leading-tight tracking-wide font-bold uppercase",
            productName?.trim()
              ? "z-hint text-black/60"
              : "z-secondary text-black"
          )}
        >
          {title}
        </h3>
        <dl className="mt-0.5 grid grid-cols-[auto_1fr] gap-x-1.5 gap-y-0 z-hint">
          <dt className="text-black/60">Serving size</dt>
          <dd className="font-medium text-black">{servingLabel}</dd>
          <dt className="text-black/60">Servings / pack</dt>
          <dd className="font-medium text-black">{servingsPerPackage}</dd>
        </dl>
      </header>

      <div className="px-2.5 pt-1">
        <div className="grid grid-cols-[1fr_42px_42px_36px] items-end gap-x-1 z-hint text-black/60 border-b border-black pb-0.5">
          <span />
          <span className="text-right">100 g</span>
          <span className="text-right">Serve</span>
          <span className="text-right">%NRV</span>
        </div>

        <ul>
          {rows.map((row, i) => {
            const isLast = i === rows.length - 1;

            return (
              <li
                key={`${row.key}-${row.unit}`}
                className={cn(
                  "grid grid-cols-[1fr_42px_42px_36px] items-center gap-x-1 py-[3px] border-b border-black/10",
                  row.indent && "pl-2",
                  isLast && "border-b-0"
                )}
              >
                <span
                  className={cn(
                    "z-hint leading-tight",
                    row.bold ? "font-semibold text-black" : "text-black/70"
                  )}
                >
                  {row.label}
                  <span className="text-black/50 ml-0.5">({row.unit})</span>
                </span>
                <span className="z-hint text-right tabular-nums text-black">
                  {fmtCell(row.per_100g, row.unit)}
                </span>
                <span className="z-hint text-right tabular-nums text-black">
                  {fmtCell(row.per_serve, row.unit)}
                </span>
                <span className="z-hint text-right tabular-nums text-black/60">
                  {row.nrv_pct === null ? "—" : `${row.nrv_pct}%`}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <footer className="px-2.5 pt-1 pb-1.5 border-t-2 border-black">
        <p className="z-hint leading-snug text-black/60">
          *NRV per Codex CAC/GL 2-1985 (FSSAI adopted) for adults on a 2 000
          kcal reference diet.
        </p>
      </footer>
    </article>
  );
};

export default NutritionLabelFSSAI;
